import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fivemLogAPI, inventoryHistoryAPI } from '../services/api';
import FiveMLogTable from '../components/FiveMLogTable';
import {
    FiUser, FiPackage, FiClock,
    FiWifi, FiWifiOff, FiArrowLeft, FiRefreshCw,
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { fmtDateTime, fmtTs } from '../utils/time';
import '../styles/FiveMPlayer.css';

const TABS = [
    { key: 'all',             label: 'Tất Cả',       icon: '📋' },
    { key: 'rvn_money_logs',  label: 'Giao Dịch',    icon: '💰' },
    { key: 'rvn_item_logs',   label: 'Vật Phẩm',     icon: '📦' },
    { key: 'rvn_admin_logs',  label: 'Admin',         icon: '🔧' },
    { key: 'rvn_damage_logs', label: 'Sát Thương',   icon: '⚔️' },
    { key: 'rvn_death_logs',  label: 'Tử Vong',      icon: '💀' },
];

const fmtMoney = n =>
    n == null || isNaN(n) ? '—' : '$' + parseFloat(n).toLocaleString('vi-VN');

const Stat = ({ label, value, color }) => (
    <div className="fvp-stat">
        <span className="fvp-stat-val" style={color ? { color } : {}}>{value}</span>
        <span className="fvp-stat-label">{label}</span>
    </div>
);

const Bar = ({ label, value, max = 100, color }) => {
    const pct = Math.min(100, Math.max(0, (value ?? 0)));
    return (
        <div className="fvp-bar-row">
            <span className="fvp-bar-label">{label}</span>
            <div className="fvp-bar-track">
                <div className="fvp-bar-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="fvp-bar-val">{value ?? '—'}%</span>
        </div>
    );
};

const FiveMPlayer = () => {
    const { citizenId } = useParams();
    const navigate = useNavigate();
    const [player, setPlayer]       = useState(null);
    const [logs, setLogs]           = useState([]);
    const [loading, setLoading]     = useState(true);
    const [logsLoading, setLogsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
    const [activeTab, setActiveTab] = useState('all');
    const [invOpen, setInvOpen]     = useState(false);
    const [invHistory, setInvHistory]       = useState([]);
    const [invHistoryOpen, setInvHistoryOpen] = useState(false);
    const [selectedSnap, setSelectedSnap]   = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);  // 'today' | 'last24h' | 'week' | 'month'

    // Full-screen spinner CHỈ cho lần mở hồ sơ đầu tiên. Đổi tab / phân trang chỉ
    // reload bảng log (logsLoading) — KHÔNG blank cả thẻ profile (tiền, dòng tiền...).
    const didInitRef = useRef(false);

    const fetchPlayer = useCallback(async (page = 1, silent = false) => {
        try {
            if (!silent) {
                if (!didInitRef.current) setLoading(true);
                else setLogsLoading(true);
            }

            const res = await fivemLogAPI.getPlayer(citizenId, {
                table: activeTab,
                page,
                limit: 50,
            });
            // Chỉ cập nhật player khi backend thực sự trả về (tránh xoá trắng thẻ profile
            // nếu 1 request lẻ trả player=null).
            if (page === 1 && res.data.player) setPlayer(res.data.player);
            setLogs(res.data.logs || []);
            setPagination(res.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
        } catch (err) {
            if (!silent) {
                console.error(err);
                toast.error('Không thể tải thông tin người chơi');
            }
        } finally {
            if (!silent) {
                setLoading(false);
                setLogsLoading(false);
                didInitRef.current = true;
            }
        }
    }, [citizenId, activeTab]);

    // Mỗi khi đổi sang player khác → cho phép full-screen spinner lại
    useEffect(() => { didInitRef.current = false; }, [citizenId]);

    useEffect(() => { fetchPlayer(1); }, [fetchPlayer]);

    // Fetch inventory history (lịch sử mở kho)
    const fetchInvHistory = useCallback(async () => {
        try {
            const res = await inventoryHistoryAPI.list(citizenId, { limit: 30 });
            setInvHistory(res.data.opens || []);
        } catch (err) {
            console.error(err);
        }
    }, [citizenId]);

    useEffect(() => { fetchInvHistory(); }, [fetchInvHistory]);

    const openSnapshot = async (id) => {
        try {
            const res = await inventoryHistoryAPI.snapshot(id);
            setSelectedSnap(res.data);
        } catch {
            toast.error('Không tải được snapshot');
        }
    };

    const INV_TYPE_LABEL = {
        player:   '🎒 Kho cá nhân',
        stash:    '📦 Stash',
        trunk:    '🚗 Cốp xe',
        glovebox: '🧤 Hộc xe',
        drop:     '💼 Túi rớt',
        shop:     '🏪 Shop',
    };

    // Auto-refresh mỗi 5s (silent — không show spinner để tránh nhấp nháy)
    useEffect(() => {
        const id = setInterval(() => fetchPlayer(pagination.page || 1, true), 5000);
        return () => clearInterval(id);
    }, [fetchPlayer, pagination.page]);

    if (loading) return (
        <div className="fvp-page">
            <div className="flt-state">
                <div className="loading-spinner" />
                <p>Đang tải hồ sơ người chơi...</p>
            </div>
        </div>
    );

    const p = player || {};
    const inventory = Array.isArray(p.inventory) ? p.inventory : [];

    return (
        <div className="fvp-page">
            {/* Back button */}
            <button className="fvp-back-btn" onClick={() => navigate(-1)}>
                <FiArrowLeft /> Quay lại
            </button>

            {/* Player card */}
            <div className="fvp-card">
                <div className="fvp-card-header">
                    <div className="fvp-avatar">
                        <FiUser />
                    </div>
                    <div className="fvp-identity">
                        <h2 className="fvp-name">
                            {p.name || citizenId}
                            {p.isAdmin && (
                                <span
                                    className={`fvp-admin-badge admin-${p.adminLevel || 'admin'}`}
                                    title={`Quyền ${(p.adminLevel || 'admin').toUpperCase()}`}
                                >
                                    {(p.adminLevel || 'admin').toUpperCase() === 'GOD' ? '👑 GOD' : '🛡️ ADMIN'}
                                </span>
                            )}
                            {p._derived && <span className="fvp-derived-tag" title="Thông tin suy ra từ logs (chưa có FiveM sync)">📊 từ logs</span>}
                            {p._isLite  && <span className="fvp-derived-tag" title="Snapshot lite — derive từ logs">📊 lite</span>}
                        </h2>
                        <div className="fvp-cid">CitizenID: <code>{citizenId}</code></div>
                        <div className={`fvp-status ${p.isOnline ? 'online' : 'offline'}`}>
                            {p.isOnline
                                ? <><FiWifi /> Online · Server ID {p.serverId}</>
                                : <><FiWifiOff /> Offline {p._lastLogActivity || p.lastSeenAt
                                        ? `· hoạt động cuối ${formatDistanceToNow(new Date(p._lastLogActivity || p.lastSeenAt), { addSuffix: true, locale: vi })}`
                                        : ''}</>
                            }
                        </div>
                    </div>
                    <button className="fvp-refresh-btn" onClick={() => fetchPlayer(1)}>
                        <FiRefreshCw />
                    </button>
                </div>

                {/* Stats row */}
                <div className="fvp-stats-row">
                    <Stat label="Tiền mặt"  value={fmtMoney(p.cash)}   color="#34d399" />
                    <Stat label="Ngân hàng" value={fmtMoney(p.bank)}   color="#60a5fa" />
                    <Stat label="Crypto"    value={fmtMoney(p.crypto)} color="#f59e0b" />
                    <div className="fvp-stat-divider" />
                    <Stat label="Công việc" value={p.jobLabel || p.jobName || '—'} />
                    <Stat label="Băng nhóm" value={p.gangLabel || p.gangName || '—'} />
                    <Stat label="Xe"        value={p.vehicleCount ?? '—'} />
                    <div className="fvp-stat-divider" />
                    <Stat label="Tù" value={p.inPrison ? '✅ Đang tù' : 'Không'} color={p.inPrison ? '#f87171' : undefined} />
                    <Stat label="Chết" value={p.isDead ? '✅ Đang chết' : 'Không'} color={p.isDead ? '#f87171' : undefined} />
                </div>

                {/* Bars */}
                {(p.hunger != null || p.thirst != null || p.stress != null || p.armor != null) && (
                    <div className="fvp-bars">
                        <Bar label="Đói"    value={p.hunger} color="#f59e0b" />
                        <Bar label="Khát"   value={p.thirst} color="#60a5fa" />
                        <Bar label="Stress" value={p.stress} color="#f87171" />
                        <Bar label="Giáp"   value={p.armor}  color="#34d399" />
                    </div>
                )}

                {/* Derived stats từ logs — 4 period cards */}
                {p._derivedStats && (
                    <div className="fvp-derived-section">
                        <div className="fvp-derived-title">
                            📊 Dòng tiền theo thời gian
                            <span className="fvp-derived-hint">· Click vào card để xem chi tiết breakdown</span>
                        </div>
                        <div className="fvp-period-grid">
                            {[
                                { key: 'today',   label: 'Hôm Nay',  icon: '☀️' },
                                { key: 'last24h', label: '24 Giờ',   icon: '🕐' },
                                { key: 'week',    label: '7 Ngày',   icon: '📅' },
                                { key: 'month',   label: '30 Ngày',  icon: '📆' },
                            ].map(per => {
                                const s = p._derivedStats[per.key];
                                if (!s) return null;
                                const netClass = s.netPure > 0 ? 'fvp-net-up' : s.netPure < 0 ? 'fvp-net-down' : '';
                                return (
                                    <button
                                        key={per.key}
                                        className={`fvp-period-card ${selectedPeriod === per.key ? 'active' : ''}`}
                                        onClick={() => setSelectedPeriod(per.key)}
                                    >
                                        <div className="fvp-period-head">
                                            <span>{per.icon} {per.label}</span>
                                            <span className="fvp-period-tx">{s.inflowCount + s.outflowCount} GD</span>
                                        </div>
                                        <div className="fvp-period-flow">
                                            <span className="fvp-flow-in">+{fmtMoney(s.inflowPure)}</span>
                                            <span className="fvp-flow-out">-{fmtMoney(s.outflowPure)}</span>
                                        </div>
                                        <div className={`fvp-period-net ${netClass}`}>
                                            NET: {s.netPure >= 0 ? '+' : ''}{fmtMoney(s.netPure)}
                                        </div>
                                        {s.internalCount > 0 && (
                                            <div className="fvp-period-int" title="Cặp rút/gửi nội bộ — đã loại khỏi NET">
                                                ↻ {s.internalCount} ATM/internal
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Inventory toggle */}
                {inventory.length > 0 && (
                    <div className="fvp-inv-section">
                        <button className="fvp-inv-toggle" onClick={() => setInvOpen(v => !v)}>
                            <FiPackage /> Kho đồ hiện tại ({inventory.length} loại) {invOpen ? '▲' : '▼'}
                        </button>
                        {invOpen && (
                            <div className="fvp-inv-grid">
                                {inventory.map((item, i) => (
                                    <div key={i} className="fvp-inv-item" title={item.name}>
                                        <span className="fvp-inv-name">{item.label || item.name || '?'}</span>
                                        <span className="fvp-inv-qty">×{item.count ?? item.amount ?? 1}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Inventory open history (snapshot mỗi lần player mở kho) */}
                {invHistory.length > 0 && (
                    <div className="fvp-inv-section">
                        <button className="fvp-inv-toggle" onClick={() => setInvHistoryOpen(v => !v)}>
                            <FiClock /> Lịch sử mở kho ({invHistory.length}) {invHistoryOpen ? '▲' : '▼'}
                        </button>
                        {invHistoryOpen && (
                            <div className="fvp-invh-list">
                                {invHistory.map(h => (
                                    <button
                                        key={h._id}
                                        className={`fvp-invh-item ${selectedSnap?._id === h._id ? 'active' : ''}`}
                                        onClick={() => openSnapshot(h._id)}
                                        title={fmtDateTime(h.openedAt)}
                                    >
                                        <span className="fvp-invh-type">{INV_TYPE_LABEL[h.inventoryType] || h.inventoryType}</span>
                                        <span className="fvp-invh-time">{formatDistanceToNow(new Date(h.openedAt), { addSuffix: true, locale: vi })}</span>
                                        <span className="fvp-invh-count">{h.itemCount} loại</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Period detail modal — breakdown by reason + currency */}
            {selectedPeriod && p._derivedStats?.[selectedPeriod] && (() => {
                const s = p._derivedStats[selectedPeriod];
                const periodLabels = { today: 'Hôm Nay', last24h: '24 Giờ Qua', week: '7 Ngày Qua', month: '30 Ngày Qua' };
                return (
                    <div className="fvp-snap-overlay" onClick={() => setSelectedPeriod(null)}>
                        <div className="fvp-snap-modal fvp-period-modal" onClick={e => e.stopPropagation()}>
                            <div className="fvp-snap-header">
                                <div>
                                    <h3>📊 Chi Tiết Dòng Tiền · {periodLabels[selectedPeriod]}</h3>
                                    <p className="fvp-snap-meta">
                                        {s.inflowCount} GD vào · {s.outflowCount} GD ra
                                        {s.internalCount > 0 && ` · ${s.internalCount} cặp ATM/internal (đã loại khỏi NET)`}
                                    </p>
                                </div>
                                <button className="fvp-snap-close" onClick={() => setSelectedPeriod(null)}>✕</button>
                            </div>

                            {/* Tổng quan */}
                            <div className="fvp-modal-overview">
                                <div className="fvp-mo-row">
                                    <span>Tổng thu (raw)</span>
                                    <span className="flt-amount amount-add">+{fmtMoney(s.inflow)}</span>
                                </div>
                                <div className="fvp-mo-row">
                                    <span>Tổng chi (raw)</span>
                                    <span className="flt-amount amount-remove">-{fmtMoney(s.outflow)}</span>
                                </div>
                                <div className="fvp-mo-row fvp-mo-divider">
                                    <span>NET (đã loại internal)</span>
                                    <span className={`flt-amount ${s.netPure >= 0 ? 'amount-add' : 'amount-remove'}`}>
                                        {s.netPure >= 0 ? '+' : ''}{fmtMoney(s.netPure)}
                                    </span>
                                </div>
                                <div className="fvp-mo-row fvp-mo-sub">
                                    <span>Thu thật (loại internal)</span>
                                    <span>+{fmtMoney(s.inflowPure)}</span>
                                </div>
                                <div className="fvp-mo-row fvp-mo-sub">
                                    <span>Chi thật (loại internal)</span>
                                    <span>-{fmtMoney(s.outflowPure)}</span>
                                </div>
                            </div>

                            {/* Breakdown by currency */}
                            {Object.keys(s.byCurrency || {}).length > 0 && (
                                <div className="fvp-modal-section">
                                    <h4>💰 Theo Loại Tiền</h4>
                                    <table className="fvp-modal-table">
                                        <thead>
                                            <tr>
                                                <th>Loại</th>
                                                <th>Vào</th>
                                                <th>Ra</th>
                                                <th>Net</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(s.byCurrency).map(([cur, v]) => (
                                                <tr key={cur}>
                                                    <td><span className={`flt-bd-pill flt-bd-${cur}`}>{cur}</span></td>
                                                    <td className="flt-amount amount-add">+{fmtMoney(v.in)}</td>
                                                    <td className="flt-amount amount-remove">-{fmtMoney(v.out)}</td>
                                                    <td className={`flt-amount ${v.in - v.out >= 0 ? 'amount-add' : 'amount-remove'}`}>
                                                        {v.in - v.out >= 0 ? '+' : ''}{fmtMoney(v.in - v.out)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Top reasons */}
                            {s.topReasons?.length > 0 && (
                                <div className="fvp-modal-section">
                                    <h4>📋 Top Lý Do (sắp xếp theo tổng impact)</h4>
                                    <table className="fvp-modal-table">
                                        <thead>
                                            <tr>
                                                <th>Lý Do</th>
                                                <th>Vào</th>
                                                <th>Ra</th>
                                                <th>Net</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {s.topReasons.map((r, i) => (
                                                <tr key={i}>
                                                    <td className="fvp-reason" title={r.reason}>{r.reason}</td>
                                                    <td className="flt-amount amount-add">
                                                        {r.inAmount > 0 ? <>+{fmtMoney(r.inAmount)} <small>×{r.inCount}</small></> : '—'}
                                                    </td>
                                                    <td className="flt-amount amount-remove">
                                                        {r.outAmount > 0 ? <>-{fmtMoney(r.outAmount)} <small>×{r.outCount}</small></> : '—'}
                                                    </td>
                                                    <td className={`flt-amount ${r.net >= 0 ? 'amount-add' : 'amount-remove'}`}>
                                                        {r.net >= 0 ? '+' : ''}{fmtMoney(r.net)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Snapshot detail modal */}
            {selectedSnap && (
                <div className="fvp-snap-overlay" onClick={() => setSelectedSnap(null)}>
                    <div className="fvp-snap-modal" onClick={e => e.stopPropagation()}>
                        <div className="fvp-snap-header">
                            <div>
                                <h3>{INV_TYPE_LABEL[selectedSnap.inventoryType] || selectedSnap.inventoryType}</h3>
                                <p className="fvp-snap-meta">
                                    {(() => { const t = fmtTs(selectedSnap.openedAt); return `${t.time} · ${t.date}`; })()}
                                    {' · '}{selectedSnap.itemCount} loại
                                    {selectedSnap.totalWeight ? ` · ${(selectedSnap.totalWeight/1000).toFixed(1)}kg` : ''}
                                </p>
                            </div>
                            <button className="fvp-snap-close" onClick={() => setSelectedSnap(null)}>✕</button>
                        </div>
                        <div className="fvp-snap-grid">
                            {(selectedSnap.items || []).map((it, i) => (
                                <div key={i} className="fvp-snap-item" title={it.name}>
                                    <div className="fvp-snap-slot">#{it.slot}</div>
                                    <div className="fvp-snap-name">{it.label || it.name}</div>
                                    <div className="fvp-snap-qty">×{it.count}</div>
                                </div>
                            ))}
                            {(selectedSnap.items || []).length === 0 && (
                                <div className="fvp-snap-empty">Kho trống</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Log section */}
            <div className="fvp-logs-section">
                <div className="fvp-logs-header">
                    <h3>Lịch Sử Hoạt Động</h3>
                    <span className="fvp-log-count">{pagination.total?.toLocaleString()} bản ghi</span>
                </div>

                {/* Tabs */}
                <div className="fvp-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            className={`fvp-tab ${activeTab === t.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                <FiveMLogTable
                    logs={logs}
                    loading={logsLoading}
                    pagination={pagination}
                    onPageChange={(page) => fetchPlayer(page)}
                    table={activeTab}
                />
            </div>
        </div>
    );
};

export default FiveMPlayer;
